import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { generateButtonClassNames } from 'ui';
import ButterWYSIWYGContainer from '~/components/ButterCMS/ButterWYSIWYGContainer';
import NewTabLink from '~/components/Links/NewTabLink';
import useFacetFiltering from '~/contexts/facetFiltering/useFacetFiltering';
import { getButterFAQByLanderId } from '~/lib/buttercms/searchPages/faq';
import { RailsBlock } from '~/typings/services/rails/tile';

function getSectionId(title: string) {
  return (
    title
      .trim()
      .toLowerCase()
      // Remove emojis
      .replace(
        /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu,
        '',
      )
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
  );
}

export default function CollapsibleButterContent({
  block,
  headerType: Header = 'h3',
}: {
  block: RailsBlock;
  headerType?: 'h1' | 'h2' | 'h3';
}) {
  const { lander } = useFacetFiltering();
  const { data } = useQuery(
    ['collapsible-butter-content', lander?.id],
    () => getButterFAQByLanderId(lander?.id!),
    {
      enabled: !!lander?.id,
    },
  );

  if (!data) {
    return null;
  }

  const sections = data?.fields?.faqbuying_guide_sections || [];
  const tableOfContents = sections?.filter(section => section.fields.title);
  if (sections.length === 0) {
    return null;
  }

  const ArticleHeader = Header === 'h2' ? 'h3' : 'h4';

  return (
    <section className="mx-auto max-w-3xl px-4" id="collapsible-content">
      {!!block.title && <Header className="mb-4 text-xl">{block.title}</Header>}

      {tableOfContents.length > 1 && (
        <div className="mb-4 rounded border px-4 py-2">
          <div className="mb-2 text-lg" id="collapsible-content-header">
            Contents
          </div>

          <ul className="space-y-2">
            {tableOfContents?.map((section, index) => (
              <li key={index}>
                <a
                  href={`#${encodeURIComponent(getSectionId(section.fields.title))}`}
                  className="text-green-500 hover:underline"
                >
                  {section.fields.title}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {sections?.map((section, index) => (
        <React.Fragment key={index}>
          <article>
            <ArticleHeader
              className="-mt-16 mb-4 pt-16 text-xl font-semibold"
              id={getSectionId(section.fields.title)}
            >
              {section.fields.title}
            </ArticleHeader>

            <ButterWYSIWYGContainer>
              <p
                dangerouslySetInnerHTML={{ __html: section.fields.content }}
                className="prose max-w-none"
              />
            </ButterWYSIWYGContainer>

            {section.fields.action_label && section.fields.action_url && (
              <div className="mb-2 mt-4">
                <NewTabLink
                  href={section.fields.action_url}
                  className={generateButtonClassNames({
                    variant: 'outlined',
                    className: 'inline-block',
                  })}
                >
                  {section.fields.action_label}
                </NewTabLink>
              </div>
            )}
          </article>

          {index < data.fields.faqbuying_guide_sections.length - 1 && (
            <hr className="my-8" />
          )}
        </React.Fragment>
      ))}
    </section>
  );
}
